import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import { additionalRoutes, navbar, type SubnavItem } from 'configs/navbar'
import { useRouter } from 'next/router'
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from '@heroicons/react/outline'
import Tooltip from 'components/base/Tooltip'
import ItemGroup from './ItemGroup'
import NavLink from './NavLink'
import { memo, useRef } from 'react'
import Link from 'components/base/Link'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  bodyWidthAtom,
  sidebarIsCollapsibleAtom,
  sidebarIsLargeAtom,
  sidebarSizeSettingAtom,
  subnavPaneContentsAtom,
  subnavPaneVerticalOffsetAtom,
} from 'components/sidebar/lib/atoms'
import SubnavPane from 'components/sidebar/SubnavPane'
import { navItemIsActive } from 'components/sidebar/lib/navItems'
import { useTheme } from 'lib/colors'
import { isLocalhost } from 'lib/contextCreation'
import ThemePicker from 'components/sidebar/ThemePicker'

type Props = {
  setMallsOpen: (to: (prev: boolean) => boolean) => void
}

const Sidebar: FC<Props> = memo(({ setMallsOpen }) => {
  const ctx = useUserContext()
  const { user, currentMall, theme } = ctx
  const { t } = useIntl()
  const router = useRouter()
  const updateSidebarSizeSetting = useSetAtom(sidebarSizeSettingAtom)
  const sidebarIsLarge = useAtomValue(sidebarIsLargeAtom)
  const sidebarIsCollapsible = useAtomValue(sidebarIsCollapsibleAtom)
  const setSubnavPaneContents = useSetAtom(subnavPaneContentsAtom)
  const setSubnavPaneVerticalOffset = useSetAtom(subnavPaneVerticalOffsetAtom)
  const { isDark } = useTheme()
  const bodyWidth = useAtomValue(bodyWidthAtom)
  const logoVerticalOffset = useRef(0)
  const localThemePickerActiveTimeout = useRef<NodeJS.Timeout | null>(null)
  const localThemePickerEnabled = isLocalhost(ctx.host)

  const help = additionalRoutes.find(
    (item) => item.name === 'help' && item.hosts?.includes(theme.hostType)
  )!
  const exports = additionalRoutes.find((item) => item.name === 'pages.exports')!
  const language = additionalRoutes.find((item) => item.name === 'language')!
  const settings = additionalRoutes.find((item) => item.name === 'settings')!
  const themePickerItems: SubnavItem[] = [
    {
      name: 'theme',
      route: '',
      active: [],
      role: 'ROLE_USER',
      addMall: false,
    },
    {
      name: 'theme_picker',
      route: '',
      active: [],
      role: 'ROLE_USER',
      addMall: false,
      customRender: () => <ThemePicker />,
    },
    {
      name: 'placeholder',
      route: '',
      active: [],
      role: 'ROLE_USER',
      addMall: false,
      customRender: () => <div></div>,
    },
  ]

  return (
    <nav className="ring-1 ring-gray-200 ring-black/10 sticky z-[1001] top-0 left-0 h-screen shadow-sm">
      <div
        onMouseLeave={() => setSubnavPaneContents(null)}
        className="relative flex flex-col w-12 h-full py-3 bg-white place-content-start large-sidebar:w-56"
      >
        <SubnavPane
          style={{
            left: !sidebarIsLarge
              ? `calc((max(${bodyWidth}px, 1920px) - 1920px) / 2 + 3rem)`
              : `calc((max(${bodyWidth}px, 1920px) - 1920px) / 2 + 14rem)`,
          }}
          className="fixed z-[-1] top-0"
        />
        <ItemGroup>
          <Link
            href="/products/list/"
            onMouseEnter={() => {
              if (localThemePickerEnabled) {
                localThemePickerActiveTimeout.current = setTimeout(() => {
                  setSubnavPaneContents(themePickerItems)
                  setSubnavPaneVerticalOffset(logoVerticalOffset.current)
                }, 150)
                return
              }

              setSubnavPaneContents(null)
            }}
            onMouseLeave={() =>
              localThemePickerActiveTimeout.current
                ? clearTimeout(localThemePickerActiveTimeout.current)
                : {}
            }
          >
            <img
              ref={(el) => {
                if (el) {
                  const rect = el.getBoundingClientRect()
                  logoVerticalOffset.current = rect.top
                }
              }}
              className="p-2.5 large-sidebar:hidden"
              src={isDark ? theme.logoFixedWhite : theme.logoFixed}
              alt={`${theme.title}`}
            />
            <div
              ref={(el) => {
                if (el) {
                  const rect = el.getBoundingClientRect()
                  logoVerticalOffset.current = rect.top
                }
              }}
              className="hidden pt-3 pb-4 pl-2 large-sidebar:block"
            >
              <img
                className={c`${theme.hostType === 'vtb'} w-24 | ${
                  theme.hostType === 'mayak'
                } h-7 w-auto | w-40`}
                src={isDark ? theme.logoWhite : theme.logoDark}
                alt={`${theme.title}`}
              />
            </div>
          </Link>
        </ItemGroup>
        <ItemGroup className="mt-1 large-sidebar:mt-1">
          {navbar.map((nav) => {
            return (
              (nav.customVisibilityCondition?.(ctx) ||
                (!nav.customVisibilityCondition &&
                  user.roles.includes(nav.role) &&
                  (!nav.malls || nav.malls.includes(currentMall.code)) &&
                  (!nav.hosts || nav.hosts.includes(theme.hostType)) &&
                  nav.items.some(
                    (item) =>
                      item.customVisibilityCondition?.(ctx) ||
                      (!item.customVisibilityCondition &&
                        user.roles.includes(item.role) &&
                        (!item.malls || item.malls.includes(currentMall.code)) &&
                        (!item.hosts || item.hosts.includes(theme.hostType)))
                  ))) && <NavLink key={nav.name} nav={nav} active={navItemIsActive(nav, router)} />
            )
          })}
        </ItemGroup>
        <ItemGroup className="mt-auto large-sidebar:px-0">
          {exports && <NavLink nav={exports} active={navItemIsActive(exports, router)} />}
          {help && <NavLink nav={help} active={navItemIsActive(help, router)} />}
          {theme.hostType !== 'psb' && (
            <NavLink nav={language} active={navItemIsActive(language, router)} />
          )}
          {theme.hostType === 'psb' && (
            <Tooltip content={t`sidebar.psb_business_site`} side="right" className="large-sidebar:hidden">
              <a
                href={
                  process.env.NODE_ENV === 'development'
                    ? 'https://devpsb.pvcd.ru'
                    : 'https://psb.pvcd.ru'
                }
                className="relative w-full large-sidebar:px-2"
              >
                <div className="flex items-center justify-center w-full py-3 group large-sidebar:w-full large-sidebar:hover:bg-gray-50 large-sidebar:rounded-md large-sidebar:py-2 large-sidebar:px-3 large-sidebar:justify-start">
                  <ExternalLinkIcon
                    className={c`w-6 h-6 text-gray-500 transition-colors duration-200 group-hover:text-gray-900 large-sidebar:text-gray-400 large-sidebar:group-hover:text-gray-500 group-hover:duration-100 large-sidebar:-ml-1 large-sidebar:mr-3 shrink-0`}
                  />
                  <span
                    className={c`large-sidebar:block group-hover:text-gray-900 group-hover:duration-100 hidden font-medium text-gray-600 transition-colors duration-200`}
                  >
                    {t`sidebar.psb_business_site`}
                  </span>
                </div>
              </a>
            </Tooltip>
          )}
          <NavLink nav={settings} active={navItemIsActive(settings, router)}>
            {user.switched && (
              <div className="absolute bg-red-500 rounded-full top-1 right-1 size-2" />
            )}
          </NavLink>
          {sidebarIsCollapsible && (
            <Tooltip
              content={sidebarIsLarge ? t`collapse` : t`expand`}
              side="right"
              className="large-sidebar:hidden"
            >
              <button
                className={c`relative w-full`}
                onClick={() => updateSidebarSizeSetting(sidebarIsLarge ? 'compact' : 'large')}
                onMouseEnter={() => setSubnavPaneContents(null)}
              >
                <div
                  className={c`flex justify-center items-center py-3 w-full group large-sidebar:w-full large-sidebar:hover:bg-gray-50 large-sidebar:rounded-md large-sidebar:py-2 large-sidebar:px-3 large-sidebar:justify-start`}
                >
                  {sidebarIsLarge ? (
                    <ChevronLeftIcon
                      className={`w-6 h-6 text-gray-500 transition-colors duration-200 group-hover:text-gray-900 large-sidebar:text-gray-400 large-sidebar:group-hover:text-gray-500 group-hover:duration-100 large-sidebar:-ml-1 large-sidebar:mr-3 shrink-0`}
                    />
                  ) : (
                    <ChevronRightIcon
                      className={`w-6 h-6 text-gray-500 transition-colors duration-200 group-hover:text-gray-900 large-sidebar:text-gray-400 large-sidebar:group-hover:text-gray-500 group-hover:duration-100 large-sidebar:-ml-1 large-sidebar:mr-3 shrink-0`}
                    />
                  )}
                  <span
                    className={c`hidden font-medium text-gray-600 transition-colors duration-200 large-sidebar:block group-hover:text-gray-900 group-hover:duration-100`}
                  >
                    {sidebarIsLarge ? t`collapse` : t`expand`}
                  </span>
                </div>
              </button>
            </Tooltip>
          )}
        </ItemGroup>
      </div>
    </nav>
  )
})

Sidebar.displayName = 'Sidebar'
export default Sidebar
